import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar as CalendarIcon, ChevronLeft, ChevronRight, MapPin, Clock, User, FileText, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/apiError';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const FULL_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const fmtSGD = (n) => `S$${Number(n || 0).toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const pad = (n) => String(n).padStart(2, '0');
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const Schedule = () => {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(toISO(today));

  const monthStart = new Date(year, month, 1);
  const monthEnd = new Date(year, month + 1, 0);
  const firstDow = monthStart.getDay();
  const daysInMonth = monthEnd.getDate();

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/schedule/jobs`, {
        params: { date_from: toISO(monthStart), date_to: toISO(monthEnd) },
      });
      setJobs(res.data || []);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load schedule'));
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  // Map jobs by date
  const jobsByDate = useMemo(() => {
    const map = {};
    jobs.forEach(j => {
      const d = j.delivery_date;
      if (!d) return;
      (map[d] = map[d] || []).push(j);
    });
    return map;
  }, [jobs]);

  // Day cells (with empty leading slots so the 1st falls under the right weekday)
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const prevMonth = () => {
    const m = month - 1;
    if (m < 0) { setMonth(11); setYear(year - 1); } else setMonth(m);
  };
  const nextMonth = () => {
    const m = month + 1;
    if (m > 11) { setMonth(0); setYear(year + 1); } else setMonth(m);
  };

  const selectedJobs = jobsByDate[selectedDate] || [];
  const todayISO = toISO(today);

  return (
    <div className="min-h-screen bg-gray-50 pt-40 pb-12">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="flex items-center gap-4 mb-6">
          <Link to="/admin"><Button variant="outline" data-testid="back-btn"><ArrowLeft size={18} /></Button></Link>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <CalendarIcon size={28} className="text-indigo-600" /> Job Schedule
            </h1>
            <p className="text-gray-600 text-sm mt-1">Calendar view of confirmed bookings — click any day to see jobs</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Calendar */}
          <Card className="lg:col-span-2">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-4">
                <Button variant="outline" size="sm" onClick={prevMonth} data-testid="prev-month"><ChevronLeft size={16} /></Button>
                <h2 className="text-xl font-bold text-gray-900">
                  {FULL_MONTHS[month]} {year}
                </h2>
                <Button variant="outline" size="sm" onClick={nextMonth} data-testid="next-month"><ChevronRight size={16} /></Button>
              </div>

              <div className="grid grid-cols-7 gap-1 text-center text-xs font-semibold text-gray-500 mb-1">
                {WEEK.map(d => <div key={d} className="py-2">{d}</div>)}
              </div>

              <div className="grid grid-cols-7 gap-1" data-testid="calendar-grid">
                {cells.map((d, i) => {
                  if (d === null) return <div key={`e${i}`} className="aspect-square" />;
                  const iso = `${year}-${pad(month + 1)}-${pad(d)}`;
                  const dayJobs = jobsByDate[iso] || [];
                  const isToday = iso === todayISO;
                  const isSelected = iso === selectedDate;
                  return (
                    <button
                      key={d}
                      onClick={() => setSelectedDate(iso)}
                      data-testid={`day-${iso}`}
                      className={`aspect-square rounded-lg border p-1 text-left transition-all hover:shadow-md ${
                        isSelected ? 'border-indigo-500 ring-2 ring-indigo-200 bg-indigo-50' :
                        isToday ? 'border-amber-400 bg-amber-50' : 'border-gray-200 bg-white'
                      }`}
                    >
                      <div className={`text-xs font-bold ${isToday ? 'text-amber-700' : 'text-gray-700'}`}>{d}</div>
                      {dayJobs.length > 0 && (
                        <div className="mt-0.5 space-y-0.5">
                          {dayJobs.slice(0, 2).map(j => (
                            <div key={j.id} className={`text-[10px] truncate px-1 py-0.5 rounded ${
                              j.delivered ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'
                            }`}>
                              {j.customer_name || j.document_number}
                            </div>
                          ))}
                          {dayJobs.length > 2 && (
                            <div className="text-[10px] text-gray-500 font-semibold">+{dayJobs.length - 2} more</div>
                          )}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Month summary */}
              <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                <div className="bg-blue-50 rounded p-2">
                  <div className="text-xl font-bold text-blue-700">{jobs.length}</div>
                  <div className="text-[10px] text-blue-700 uppercase tracking-wider">Total jobs</div>
                </div>
                <div className="bg-emerald-50 rounded p-2">
                  <div className="text-xl font-bold text-emerald-700">{jobs.filter(j => j.delivered).length}</div>
                  <div className="text-[10px] text-emerald-700 uppercase tracking-wider">Delivered</div>
                </div>
                <div className="bg-amber-50 rounded p-2">
                  <div className="text-xl font-bold text-amber-700">{jobs.filter(j => !j.delivered).length}</div>
                  <div className="text-[10px] text-amber-700 uppercase tracking-wider">Pending</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Day detail */}
          <Card className="lg:col-span-1">
            <CardContent className="p-4">
              <h3 className="font-bold text-gray-900 mb-1">{selectedDate}</h3>
              <p className="text-xs text-gray-500 mb-3">
                {selectedJobs.length} job{selectedJobs.length === 1 ? '' : 's'} scheduled
              </p>

              {loading ? (
                <div className="text-center py-6">
                  <div className="w-6 h-6 border-4 border-gray-300 border-t-gray-900 rounded-full animate-spin mx-auto" />
                </div>
              ) : selectedJobs.length === 0 ? (
                <div className="text-center py-8 text-gray-400 text-sm">
                  <CalendarIcon size={28} className="mx-auto mb-2 text-gray-300" />
                  No jobs scheduled
                </div>
              ) : (
                <div className="space-y-2" data-testid="day-jobs">
                  {selectedJobs.map(j => (
                    <Link key={j.id} to={`/admin/delivery-orders/${j.id}`}
                          className="block border rounded-lg p-3 hover:bg-gray-50 hover:shadow-sm transition-all">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="font-mono text-xs font-bold text-gray-700">{j.document_number}</span>
                        {j.delivered ? (
                          <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">
                            <CheckCircle2 size={10} className="mr-1" /> Delivered
                          </Badge>
                        ) : (
                          <Badge className="bg-blue-100 text-blue-700 text-[10px]">Scheduled</Badge>
                        )}
                      </div>
                      <p className="text-sm font-semibold text-gray-900 flex items-center gap-1">
                        <User size={12} /> {j.customer_name}
                      </p>
                      {j.delivery_time && (
                        <p className="text-xs text-gray-600 mt-1 flex items-center gap-1">
                          <Clock size={11} /> {j.delivery_time}
                        </p>
                      )}
                      {j.pickup_address && (
                        <p className="text-xs text-gray-600 mt-1 flex items-start gap-1">
                          <MapPin size={11} className="text-emerald-600 mt-0.5" />
                          <span className="truncate">{j.pickup_address}</span>
                        </p>
                      )}
                      {j.delivery_address && j.delivery_address !== j.pickup_address && (
                        <p className="text-xs text-gray-600 flex items-start gap-1">
                          <MapPin size={11} className="text-red-600 mt-0.5" />
                          <span className="truncate">{j.delivery_address}</span>
                        </p>
                      )}
                      {j.summary && (
                        <p className="text-[11px] text-gray-500 mt-1 flex items-start gap-1">
                          <FileText size={11} className="mt-0.5" />
                          <span className="truncate">{j.summary}</span>
                        </p>
                      )}
                      <div className="mt-2 text-right">
                        <span className="text-sm font-bold text-gray-800">{fmtSGD(j.total_amount)}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Legend / Info */}
        <Card className="mt-4 border-blue-200 bg-blue-50">
          <CardContent className="py-3 text-sm text-blue-900 flex items-start gap-3">
            <CalendarIcon size={18} className="text-blue-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">How this works</p>
              <p className="text-xs mt-1">
                Confirmed bookings auto-create a Delivery Order with the date/time the customer chose — and they appear here automatically.
                Customers also receive a WhatsApp reminder <b>3 days before</b> the job (requires Twilio setup).
                Click any day to see job details, or click a job to open its DO.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Schedule;
